import type React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogBody,
  Button,
  Input,
  Spinner,
  Text,
  Avatar,
  mergeClasses,
} from '@fluentui/react-components';
import {
  SearchRegular,
  PersonRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { Incident, OperatorUser } from 'types';
import { extractApiErrorMessage, getOperators, takeoverIncident } from 'services';
import { useIncidentSync } from 'hooks';
import { useUserPickerDialogStyles } from './UserPickerDialog.styles';
import { SeverityBadge } from './SeverityBadge';

interface UserPickerDialogProps {
  /** The incident to assign */
  incident: Incident | null;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when assignment is successful */
  onAssignComplete?: () => void;
}

/**
 * Dialog for selecting a SOC operator to assign an incident to.
 * Fetches the list of operators from the SOC operators security group.
 * Only available to admin users.
 */
export const UserPickerDialog: React.FC<UserPickerDialogProps> = ({
  incident,
  open,
  onClose,
  onAssignComplete,
}) => {
  const styles = useUserPickerDialogStyles();
  const { t } = useTranslation();
  const waitForIncidentSync = useIncidentSync();

  const [operators, setOperators] = useState<OperatorUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<OperatorUser | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Fetch operators when dialog opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      setSelectedUser(null);
      setSearchQuery('');

      getOperators()
        .then((users) => {
          setOperators(users);
        })
        .catch((err) => {
          console.error('Failed to fetch operators:', err);
          setError(t('statusPanel.userPicker.fetchError', 'Failed to load operators'));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, t]);

  // Filter operators by search query
  const filteredOperators = useMemo(() => {
    if (!searchQuery.trim()) {
      return operators;
    }
    const query = searchQuery.toLowerCase();
    return operators.filter(
      (user) =>
        user.displayName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        (user.jobTitle?.toLowerCase().includes(query) ?? false)
    );
  }, [operators, searchQuery]);

  const handleSelectUser = useCallback((user: OperatorUser) => {
    setSelectedUser(user);
  }, []);

  const handleAssign = useCallback(async () => {
    if (!selectedUser || !incident) return;

    setAssigning(true);
    try {
      await takeoverIncident(incident.incidentId, selectedUser.id);
      await waitForIncidentSync({ incidentId: incident.incidentId });
      onAssignComplete?.();
      onClose();
    } catch (err) {
      console.error('Failed to assign incident:', err);
      setError(extractApiErrorMessage(err, t('statusPanel.userPicker.assignError', 'Failed to assign incident')));
    } finally {
      setAssigning(false);
    }
  }, [selectedUser, incident, onAssignComplete, onClose, t]);

  const handleClose = useCallback(() => {
    if (!assigning) {
      onClose();
    }
  }, [assigning, onClose]);

  // Get initials for avatar
  const getInitials = (name: string): string => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={(_e, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={handleClose}
                disabled={assigning}
              />
            }
          >
            {t('statusPanel.userPicker.title', 'Assign Incident')}
          </DialogTitle>

          <DialogContent className={styles.content}>
            {/* Incident info */}
            {incident && (
              <div className={styles.incidentInfo}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SeverityBadge severity={incident.severity} />
                  <Text className={styles.incidentTitle}>{incident.title}</Text>
                </div>
              </div>
            )}

            {/* Search input */}
            <div className={styles.searchContainer}>
              <Input
                placeholder={t('statusPanel.userPicker.searchPlaceholder', 'Search operators...')}
                contentBefore={<SearchRegular />}
                value={searchQuery}
                onChange={(_e, data) => setSearchQuery(data.value)}
                disabled={loading}
              />
            </div>

            {/* Loading state */}
            {loading && (
              <div className={styles.loadingContainer}>
                <Spinner size="medium" label={t('statusPanel.userPicker.loading', 'Loading operators...')} />
              </div>
            )}

            {/* Error state */}
            {error && !loading && (
              <div className={mergeClasses(styles.emptyState, styles.errorState)}>
                <Text>{error}</Text>
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && filteredOperators.length === 0 && (
              <div className={styles.emptyState}>
                <PersonRegular style={{ fontSize: 32, marginBottom: 8 }} />
                <Text>
                  {searchQuery
                    ? t('statusPanel.userPicker.noResults', 'No operators match your search')
                    : t('statusPanel.userPicker.noOperators', 'No operators available')}
                </Text>
              </div>
            )}

            {/* User list */}
            {!loading && !error && filteredOperators.length > 0 && (
              <div className={styles.userList}>
                {filteredOperators.map((user) => (
                  <div
                    key={user.id}
                    className={mergeClasses(
                      styles.userItem,
                      selectedUser?.id === user.id && styles.userItemSelected
                    )}
                    onClick={() => handleSelectUser(user)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSelectUser(user)}
                    role="button"
                    tabIndex={0}
                  >
                    <Avatar
                      className={styles.avatar}
                      name={user.displayName}
                      initials={getInitials(user.displayName)}
                      size={36}
                    />
                    <div className={styles.userInfo}>
                      <Text className={styles.userName}>{user.displayName}</Text>
                      <Text className={styles.userEmail}>{user.email}</Text>
                      {user.jobTitle && (
                        <Text className={styles.userJobTitle}>{user.jobTitle}</Text>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={handleClose} disabled={assigning}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              appearance="primary"
              onClick={handleAssign}
              disabled={!selectedUser || assigning}
            >
              {assigning ? (
                <Spinner size="tiny" />
              ) : (
                t('statusPanel.userPicker.assign', 'Assign')
              )}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
